/**
 * /table · FilterBar
 *
 * Work-order v2 §5 binding (5 Jun 2026):
 *   When the member tightens chips, we RE-QUERY the live API ·
 *   we do NOT just hide cards from the in-memory set. This component
 *   exposes onChange(filters) and the parent (Table.jsx) re-runs the
 *   search with the new filter params.
 *
 *   Anti-fab discipline: cuisine pills are limited to canonical
 *   cuisines our backend cuisine_tags ever produce. Price chips map
 *   1:1 to Google Places `price_level`. Rating threshold maps 1:1
 *   to Places `rating`. Nothing here invents a constraint the
 *   backend can't honestly honour.
 */
import React from "react";

const CUISINE_PILLS = [
    "Italian", "Japanese", "Chinese", "Indian", "French", "Thai",
    "Mexican", "Mediterranean", "American", "Spanish", "Vietnamese",
    "Korean", "Vegetarian", "Vegan",
];

const PRICE_CHIPS = [
    { label: "£", value: 1, hint: "Budget" },
    { label: "££", value: 2, hint: "Mid" },
    { label: "£££", value: 3, hint: "Premium" },
    { label: "££££", value: 4, hint: "Luxury" },
];

const RATING_CHIPS = [
    { label: "Any rating", value: null },
    { label: "★ 4.0+", value: 4.0 },
    { label: "★ 4.5+", value: 4.5 },
];

const ROUTE_PILLS = [
    { key: "instant",   label: "⚡ Instant",   color: "var(--t-verified)" },
    { key: "call",      label: "☎ Call",      color: "var(--t-pending)" },
    { key: "contact",   label: "✉ Contact",   color: "var(--t-slate)" },
    { key: "concierge", label: "✦ Concierge®", color: "var(--t-brass)" },
];

const baseChip = {
    borderRadius: 30,
    padding: "5px 11px",
    fontSize: 12,
    cursor: "pointer",
    border: "1px solid var(--t-line2)",
    background: "var(--t-card)",
    color: "var(--t-ink2)",
    transition: "all 0.18s",
    whiteSpace: "nowrap",
};

function Pill({ active, onClick, children, activeColor, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            style={{
                ...baseChip,
                background: active ? "var(--t-ink)" : "var(--t-card)",
                color: active ? "var(--t-paper2)" : (activeColor || "var(--t-ink2)"),
                borderColor: active ? "var(--t-ink)" : "var(--t-line2)",
                fontWeight: active ? 600 : 400,
            }}
        >
            {children}
        </button>
    );
}

export function FilterBar({ filters, onChange, onClear, onCuisineSearch, cityCounts = null }) {
    const f = filters || {};
    const [cuisineText, setCuisineText] = React.useState("");
    const setCuisine = (c) => {
        const set = new Set((f.cuisine || "").split(",").filter(Boolean));
        if (set.has(c)) set.delete(c); else set.add(c);
        onChange({ ...f, cuisine: [...set].join(",") || null });
    };
    const setPrice = (v) => {
        // Founder doctrine 7 Jun 2026 evening · exact-tier semantic.
        // PRIOR BEHAVIOUR: this set only `price_max=v`, which meant
        // "venues priced ≤ v" · clicking ££££ alone was effectively
        // no filter (everything is ≤ ££££). The founder's mental model
        // — and any reasonable member's — is "clicking ££££ = ONLY
        // the most expensive." Setting both bounds to v gives exact
        // tier · click same chip again to clear.
        const isToggle = (f.price_max === v && f.price_min === v);
        onChange({
            ...f,
            price_min: isToggle ? null : v,
            price_max: isToggle ? null : v,
        });
    };
    const setRating = (v) => onChange({ ...f, rating_min: v });
    const setRoute = (key) => {
        const set = new Set((f.route_in || "").split(",").filter(Boolean));
        if (set.has(key)) set.delete(key); else set.add(key);
        onChange({ ...f, route_in: [...set].join(",") || null });
    };
    const toggleOpenNow = () => {
        onChange({ ...f, open_now: f.open_now ? null : true });
    };
    const setCity = (city) => {
        onChange({ ...f, city: f.city === city ? null : city });
    };
    const has = (set, v) => (set || "").split(",").includes(v);
    // Price filter now sets both min+max to the same value (exact tier).
    // Count as ONE active filter, not two · activeCount must reflect
    // user-intent count, not implementation count.
    const _priceActive = (f.price_min || f.price_max) ? 1 : 0;
    const activeCount = (
        ((f.cuisine || "").split(",").filter(Boolean).length) +
        _priceActive +
        (f.rating_min ? 1 : 0) +
        ((f.route_in || "").split(",").filter(Boolean).length) +
        (f.open_now ? 1 : 0) +
        (f.city ? 1 : 0)
    );

    // City chips · derived from the current results' city distribution.
    // Mirrors the BrowseFilterStrip's "where" group on results view so
    // the member can narrow without re-typing a city into the bar.
    const cityEntries = cityCounts
        ? Object.entries(cityCounts).filter(([, n]) => n > 0).slice(0, 8)
        : [];

    return (
        <div
            data-testid="table-filter-bar"
            className="t-fade"
            style={{
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line)",
                borderRadius: 16,
                padding: "12px 14px",
                marginBottom: 14,
            }}
        >
            <div className="flex items-center justify-between gap-3 mb-2">
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.2em",
                        color: "var(--t-ink3)",
                    }}
                >
                    Refine
                </span>
                {activeCount > 0 ? (
                    <button
                        type="button"
                        onClick={onClear}
                        data-testid="filter-clear-all"
                        style={{
                            fontSize: 11, color: "var(--t-brass)",
                            background: "var(--t-brass-wash)",
                            border: "1px solid var(--t-brass2)",
                            borderRadius: 20,
                            padding: "4px 12px",
                            cursor: "pointer",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                        }}
                    >
                        Clear all ({activeCount}) ↺
                    </button>
                ) : null}
            </div>

            {/* Route */}
            <div className="mb-2">
                <div
                    style={{
                        fontSize: 10, color: "var(--t-ink3)",
                        marginBottom: 5, letterSpacing: "0.04em",
                    }}
                >
                    Booking path
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                    {ROUTE_PILLS.map((r) => (
                        <Pill
                            key={r.key}
                            active={has(f.route_in, r.key)}
                            activeColor={r.color}
                            onClick={() => setRoute(r.key)}
                            testid={`filter-route-${r.key}`}
                        >
                            {r.label}
                        </Pill>
                    ))}
                    {/* Open Now · single chip resurrects the dead
                       `open_now=true` parameter. */}
                    <Pill
                        active={!!f.open_now}
                        onClick={toggleOpenNow}
                        testid="filter-open-now"
                    >
                        ● Open now
                    </Pill>
                </div>
            </div>

            {/* City · derived from result-set distribution (filter
               consistency, 29 Jun 2026). Only renders when the parent
               has actually surfaced more than one city in the results
               — single-city queries hide this group entirely. */}
            {cityEntries.length > 1 ? (
                <div className="mb-2" data-testid="filter-city-group">
                    <div style={{
                        fontSize: 10, color: "var(--t-ink3)",
                        marginBottom: 5, letterSpacing: "0.04em",
                    }}>
                        Where
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {cityEntries.map(([city, n]) => (
                            <Pill
                                key={city}
                                active={f.city === city}
                                onClick={() => setCity(city)}
                                testid={`filter-city-${city.toLowerCase().replace(/\W+/g, "-")}`}
                            >
                                {city} <span style={{ opacity: 0.6 }}>· {n}</span>
                            </Pill>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Cuisine — popular shortlist + universal entry */}
            <div className="mb-2">
                <div
                    className="flex items-baseline justify-between gap-2 flex-wrap"
                    style={{ marginBottom: 6 }}
                >
                    <span
                        style={{
                            fontSize: 10, color: "var(--t-ink3)",
                            letterSpacing: "0.04em",
                        }}
                    >
                        Popular cuisines
                        <span style={{ color: "var(--t-ink3)", fontStyle: "italic", marginLeft: 4 }}>
                            — or type any cuisine in search
                        </span>
                    </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {CUISINE_PILLS.map((c) => (
                        <Pill
                            key={c}
                            active={has(f.cuisine, c)}
                            onClick={() => setCuisine(c)}
                            testid={`filter-cuisine-${c.toLowerCase()}`}
                        >
                            {c}
                        </Pill>
                    ))}
                </div>
                {/* Free-typed cuisine — re-runs the LIVE search with
                    the typed cuisine as the new query, not a chip
                    filter. The honest "any cuisine" entry · doesn't
                    invent a tag, just hands a real query to Places. */}
                {onCuisineSearch ? (
                    <form
                        className="flex gap-1.5 mt-2"
                        data-testid="filter-cuisine-typeahead-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            const t = cuisineText.trim();
                            if (t.length < 2) return;
                            onCuisineSearch(t);
                            setCuisineText("");
                        }}
                    >
                        <input
                            data-testid="filter-cuisine-typeahead"
                            value={cuisineText}
                            onChange={(e) => setCuisineText(e.target.value)}
                            placeholder="Type any cuisine — Turkish · Ethiopian · Peruvian…"
                            style={{
                                flex: 1, minWidth: 0,
                                background: "var(--t-paper)",
                                border: "1px solid var(--t-line2)",
                                borderRadius: 20,
                                padding: "5px 12px",
                                fontSize: 12,
                                color: "var(--t-ink)",
                                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                                outline: "none",
                            }}
                        />
                        <button
                            type="submit"
                            data-testid="filter-cuisine-typeahead-go"
                            disabled={cuisineText.trim().length < 2}
                            style={{
                                fontSize: 11, fontWeight: 600,
                                color: cuisineText.trim().length < 2 ? "var(--t-ink3)" : "var(--t-paper2)",
                                background: cuisineText.trim().length < 2 ? "var(--t-card)" : "var(--t-ink)",
                                border: "1px solid var(--t-ink)",
                                borderRadius: 20,
                                padding: "5px 12px",
                                cursor: cuisineText.trim().length < 2 ? "not-allowed" : "pointer",
                                letterSpacing: "0.02em",
                            }}
                        >
                            Search
                        </button>
                    </form>
                ) : null}
            </div>

            {/* Price + Rating · single row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                    <div
                        style={{
                            fontSize: 10, color: "var(--t-ink3)",
                            marginBottom: 5, letterSpacing: "0.04em",
                        }}
                    >
                        Price up to
                    </div>
                    <div className="flex gap-1.5">
                        {PRICE_CHIPS.map((p) => (
                            <Pill
                                key={p.value}
                                active={f.price_max === p.value && f.price_min === p.value}
                                onClick={() => setPrice(p.value)}
                                testid={`filter-price-${p.value}`}
                            >
                                {p.label}
                            </Pill>
                        ))}
                    </div>
                </div>
                <div>
                    <div
                        style={{
                            fontSize: 10, color: "var(--t-ink3)",
                            marginBottom: 5, letterSpacing: "0.04em",
                        }}
                    >
                        Rating
                    </div>
                    <div className="flex gap-1.5">
                        {RATING_CHIPS.map((r) => (
                            <Pill
                                key={String(r.value)}
                                active={(f.rating_min || null) === r.value}
                                onClick={() => setRating(r.value)}
                                testid={`filter-rating-${r.value || "any"}`}
                            >
                                {r.label}
                            </Pill>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
